import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { ApiError, api } from '../lib/api';
import { chunk, parseCsv, type ImportRow } from '../lib/csv';

const BATCH_SIZE = 100;

export function ImportPage() {
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [summary, setSummary] = useState<
    null | { created: number; updated: number; skipped: number; errors: unknown[] }
  >(null);
  const [importError, setImportError] = useState<string | null>(null);

  const importMut = useMutation({
    mutationFn: async (allRows: ImportRow[]) => {
      const batches = chunk(allRows, BATCH_SIZE);
      const merged = { created: 0, updated: 0, skipped: 0, errors: [] as unknown[] };
      // Render a "0/N" state immediately so the panel shows even before the
      // first batch resolves.
      setProgress({ done: 0, total: allRows.length });
      await new Promise((r) => setTimeout(r, 0)); // yield to let React paint
      for (let i = 0; i < batches.length; i += 1) {
        const batch = batches[i];
        if (!batch) continue;
        const result = await api.importCsv(batch as unknown[]);
        merged.created += result.created;
        merged.updated += result.updated;
        merged.skipped += result.skipped;
        merged.errors.push(...(result.errors as unknown[]));
        setProgress({
          done: Math.min((i + 1) * BATCH_SIZE, allRows.length),
          total: allRows.length,
        });
      }
      return merged;
    },
    onMutate: () => {
      setImportError(null);
      setSummary(null);
    },
    onSuccess: (result) => {
      setSummary(result);
      setProgress(null);
    },
    onError: (err) => {
      setProgress(null);
      if (err instanceof ApiError) {
        setImportError(
          `HTTP ${err.status} — ${
            typeof err.detail === 'string' ? err.detail : JSON.stringify(err.detail, null, 2)
          }`,
        );
      } else {
        setImportError(err instanceof Error ? err.message : String(err));
      }
      console.error('Import failed', err);
    },
  });

  // Warn the user before navigating away while an import is in flight.
  useEffect(() => {
    if (!importMut.isPending) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [importMut.isPending]);

  return (
    <>
      <div className="card">
        <p style={{ marginTop: 0 }}>
          Upload the Google Sheet export. Rows are upserted on <code>inventory_id</code> — re-running the
          import is safe.
        </p>
        <p style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>
          An import is too large to mirror row by row, so it does not update the Google Sheet.
          Run <strong>Settings → Google Sheet → Rebuild sheet</strong> afterwards.
        </p>

        <input
          type="file"
          accept=".csv,text/csv"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            const { rows, errors } = await parseCsv(file);
            setRows(rows);
            setParseErrors(errors);
            setSummary(null);
            setImportError(null);
          }}
        />

        {rows.length > 0 && !importMut.isPending && !summary ? (
          <div style={{ marginTop: '1rem' }}>
            <p>
              Parsed <strong>{rows.length}</strong> rows.
              {parseErrors.length > 0 ? (
                <span style={{ color: 'var(--warn)' }}> {parseErrors.length} warning(s).</span>
              ) : null}
            </p>
            <button className="primary" onClick={() => importMut.mutate(rows)}>
              Import {rows.length} rows
            </button>
          </div>
        ) : null}

        {importMut.isPending && progress ? (
          <ImportProgressPanel done={progress.done} total={progress.total} />
        ) : null}

        {importError ? (
          <div className="card" style={{ marginTop: '1rem', borderColor: 'var(--danger)' }}>
            <strong style={{ color: 'var(--danger)' }}>Import failed</strong>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', marginTop: '0.5rem' }}>
              {importError}
            </pre>
          </div>
        ) : null}

        {summary ? (
          <div style={{ marginTop: '1rem' }}>
            <strong>Import finished.</strong>
            <ul>
              <li>Created: {summary.created}</li>
              <li>Updated: {summary.updated}</li>
              <li>Skipped: {summary.skipped}</li>
              <li>Errors: {summary.errors.length}</li>
            </ul>
            {summary.errors.length > 0 ? (
              <details>
                <summary>Show errors</summary>
                <pre style={{ fontSize: '0.8rem' }}>{JSON.stringify(summary.errors, null, 2)}</pre>
              </details>
            ) : null}
          </div>
        ) : null}

        {parseErrors.length > 0 ? (
          <details style={{ marginTop: '1rem' }}>
            <summary>Parse warnings ({parseErrors.length})</summary>
            <ul>
              {parseErrors.map((e, i) => (
                <li key={i} style={{ color: 'var(--warn)' }}>
                  {e}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </div>
    </>
  );
}

function ImportProgressPanel({ done, total }: { done: number; total: number }) {
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  return (
    <div
      style={{
        marginTop: '1rem',
        padding: '1.25rem',
        borderRadius: 'var(--radius)',
        background: 'color-mix(in srgb, var(--primary) 8%, transparent)',
        border: '1px solid color-mix(in srgb, var(--primary) 30%, transparent)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Spinner />
        <div style={{ fontWeight: 600 }}>
          Importing inventory — {done}/{total} rows ({pct}%)
        </div>
      </div>
      <div
        style={{
          marginTop: '0.75rem',
          height: 8,
          width: '100%',
          borderRadius: 999,
          background: 'rgba(0,0,0,0.06)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${pct}%`,
            height: '100%',
            background: 'var(--primary)',
            transition: 'width 200ms ease',
          }}
        />
      </div>
      <p style={{ marginTop: '0.75rem', marginBottom: 0, color: 'var(--muted)', fontSize: '0.85rem' }}>
        Keep this tab open until the import finishes. You can navigate away in the app — the import
        will continue — but closing or refreshing the tab will cancel any in-flight batches.
      </p>
    </div>
  );
}

function Spinner() {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 18,
        height: 18,
        border: '2px solid color-mix(in srgb, var(--primary) 30%, transparent)',
        borderTopColor: 'var(--primary)',
        borderRadius: '50%',
        animation: 'ncc-spin 0.8s linear infinite',
      }}
    />
  );
}
